"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect } from "react";

import { createClient } from "@/lib/supabase-browser";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

/** Anything inside this window of `created_at` is a fresh signup. */
const SIGNUP_FRESHNESS_MS = 5 * 60 * 1000;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!POSTHOG_KEY) return;
    if (process.env.NODE_ENV !== "production") return;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      // Off so we can fire explicit `landing_view` events with UTM params
      // attached and avoid double-counting auto-captured pageviews.
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      capture_exceptions: true,
      before_send: (event) => {
        if (event?.event === "$exception") {
          const list = event.properties?.$exception_list as
            | Array<{ type?: string; value?: string }>
            | undefined;
          if (
            list?.some(
              (ex) =>
                // Supabase auth's navigator.locks-based session refresh "steals"
                // the lock from sibling tabs by design; the loser tab throws
                // AbortError even though the refresh succeeded.
                ex.value?.includes(
                  "Lock broken by another request with the 'steal' option",
                ),
            )
          ) {
            return null;
          }
        }
        return event;
      },
    });

    const supabase = createClient();
    let currentId: string | null = null;
    const signupFired = new Set<string>();

    const detectMethod = (provider: string | undefined): "email" | "google" | "oauth" => {
      if (!provider || provider === "email") return "email";
      if (provider === "google") return "google";
      return "oauth";
    };

    const identify = async (
      user: {
        id: string;
        email?: string | null;
        created_at?: string | null;
        app_metadata?: { provider?: string };
      } | null,
    ) => {
      if (user) {
        if (currentId === user.id) return;
        currentId = user.id;
        // Identify first so subsequent capture() calls bind to this distinct id.
        posthog.identify(
          user.id,
          user.email ? { email: user.email } : undefined,
        );

        // Fresh signup detection: created_at within the last 5 minutes and we
        // haven't already fired for this user in this session.
        const createdAt = user.created_at ? Date.parse(user.created_at) : NaN;
        const isFreshSignup =
          Number.isFinite(createdAt) &&
          Date.now() - createdAt < SIGNUP_FRESHNESS_MS;
        if (isFreshSignup && !signupFired.has(user.id)) {
          signupFired.add(user.id);
          try {
            posthog.capture("signup_completed", {
              method: detectMethod(user.app_metadata?.provider),
            });
          } catch {
            /* never let analytics break auth flow */
          }
        }

        // Enrich the person profile with tier — funnel queries by tier need
        // this, and it costs one cheap API call per session.
        try {
          const res = await fetch("/api/account");
          if (res.ok) {
            const data = (await res.json()) as { tier?: string };
            if (data.tier) {
              posthog.people.set({ tier: data.tier });
            }
          }
        } catch {
          /* tier enrichment is best-effort */
        }
      } else if (currentId !== null) {
        currentId = null;
        signupFired.clear();
        posthog.reset();
      }
    };

    supabase.auth.getUser().then(({ data }) => identify(data.user ?? null));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      identify(session?.user ?? null);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
